import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, User, Calendar, Mail, Phone, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { globalSearch, GlobalSearchResponse } from "../../services/SearchApi";

interface SearchOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

const AestheticSearchOverlay = ({ isOpen, onClose }: SearchOverlayProps) => {
    const [searchValue, setSearchValue] = useState("");
    const [results, setResults] = useState<GlobalSearchResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const handleItemClick = (type: string, id: string | number) => {
        return;
        const routeMap: Record<string, string> = {
            customer: "customers",
            employee: "employees",
            service: "services",
            product: "products"
        };

        const category = routeMap[type];
        if (category) {
            navigate(`/summary/${category}/${id}`);
            onClose();
        }
    };

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => inputRef.current?.focus(), 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Handle Search API call with debounce
    useEffect(() => {
        if (!searchValue.trim()) {
            setResults(null);
            setIsLoading(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            setIsLoading(true);
            try {
                const data = await globalSearch(searchValue);
                setResults(data);
            } catch (error) {
                console.error("Search error:", error);
            } finally {
                setIsLoading(false);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(debounceTimer);
    }, [searchValue]);

    // Handle escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] bg-white animate-in fade-in duration-300">
            {/* Header with Close Button */}
            <div className="absolute top-8 right-8">
                <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900 border border-gray-200"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            <div className="max-w-6xl mx-auto px-6 pt-32 h-full overflow-y-auto">
                {/* Search Header Section */}
                <div className="mb-12">
                    <h1 className="text-5xl font-light text-gray-400 mb-6">What are you looking for?</h1>
                    <div className="relative group">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search by client name, mobile, email or booking reference"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="w-full text-2xl py-4 border-b-2 border-gray-100 focus:border-[#885ABB] outline-none transition-colors duration-300 placeholder:text-gray-200"
                        />
                        <div className="absolute right-0 bottom-4 flex items-center gap-2">
                            {isLoading && <Loader2 className="w-6 h-6 text-[#885ABB] animate-spin" />}
                            <Search className="w-8 h-8 text-gray-200 group-focus-within:text-[#885ABB] transition-colors" />
                        </div>
                    </div>
                    <p className="text-sm text-gray-400 mt-4">Press ESC to close</p>
                </div>

                {/* Results Sections */}
                {results && results.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pb-20">
                        {/* Customers */}
                        {results.filter(i => i.type === 'customer').length > 0 && (
                            <section>
                                <h2 className="text-lg font-bold text-[#402B69] mb-4 flex items-center gap-2">
                                    <span className="p-1.5 rounded-lg bg-purple-50 text-[#885ABB] border border-purple-100"><User size={18} /></span>
                                    Customers
                                </h2>
                                <div className="space-y-1">
                                    {results.filter(i => i.type === 'customer').map((item, i) => (
                                        <div
                                            key={i}
                                            onClick={() => handleItemClick('customer', item.id)}
                                            className="group p-3 rounded-xl hover:bg-purple-50 transition-all cursor-pointer border border-transparent hover:border-purple-100 flex items-center justify-between"
                                        >
                                            <div>
                                                <p className="font-semibold text-gray-900 group-hover:text-[#885ABB] text-sm">{item.name}</p>
                                                <p className="text-[10px] text-gray-500">{item.email || item.mobile || item.info}</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#885ABB] transition-colors" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Employees */}
                        {results.filter(i => i.type === 'employee').length > 0 && (
                            <section>
                                <h2 className="text-lg font-bold text-[#402B69] mb-4 flex items-center gap-2">
                                    <span className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100"><User size={18} /></span>
                                    Employees
                                </h2>
                                <div className="space-y-1">
                                    {results.filter(i => i.type === 'employee').map((item, i) => (
                                        <div
                                            key={i}
                                            onClick={() => handleItemClick('employee', item.id)}
                                            className="group p-3 rounded-xl hover:bg-indigo-50 transition-all cursor-pointer border border-transparent hover:border-indigo-100 flex items-center justify-between"
                                        >
                                            <div>
                                                <p className="font-semibold text-gray-800 group-hover:text-indigo-600 text-sm">{item.name}</p>
                                                <p className="text-[10px] text-gray-500">{item.email || item.mobile}</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-600 transition-colors" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Services */}
                        {results.filter(i => i.type === 'service').length > 0 && (
                            <section>
                                <h2 className="text-lg font-bold text-[#402B69] mb-4 flex items-center gap-2">
                                    <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-100"><Calendar size={18} /></span>
                                    Services
                                </h2>
                                <div className="space-y-1">
                                    {results.filter(i => i.type === 'service').map((item, i) => (
                                        <div
                                            key={i}
                                            onClick={() => handleItemClick('service', item.id)}
                                            className="group p-3 rounded-xl hover:bg-blue-50/50 transition-all cursor-pointer border border-transparent hover:border-blue-100 flex items-center justify-between"
                                        >
                                            <p className="font-semibold text-gray-800 group-hover:text-blue-600 text-sm">{item.name}</p>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-600 transition-colors" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Products */}
                        {results.filter(i => i.type === 'product').length > 0 && (
                            <section>
                                <h2 className="text-lg font-bold text-[#402B69] mb-4 flex items-center gap-2">
                                    <span className="p-1.5 rounded-lg bg-orange-50 text-orange-600 border border-orange-100"><Mail size={18} /></span>
                                    Products
                                </h2>
                                <div className="space-y-1">
                                    {results.filter(i => i.type === 'product').map((item, i) => (
                                        <div
                                            key={i}
                                            onClick={() => handleItemClick('product', item.id)}
                                            className="group p-3 rounded-xl hover:bg-orange-50/50 transition-all cursor-pointer border border-transparent hover:border-orange-100 flex items-center justify-between"
                                        >
                                            <p className="font-semibold text-gray-800 group-hover:text-orange-600 text-sm">{item.name}</p>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-orange-600 transition-colors" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Bookings */}
                        {results.filter(i => i.type === 'booking').length > 0 && (
                            <section>
                                <h2 className="text-lg font-bold text-[#402B69] mb-4 flex items-center gap-2">
                                    <span className="p-1.5 rounded-lg bg-green-50 text-green-600 border border-green-100"><Calendar size={18} /></span>
                                    Bookings
                                </h2>
                                <div className="space-y-1">
                                    {results.filter(i => i.type === 'booking').map((item, i) => (
                                        <div key={i} className="group p-3 rounded-xl hover:bg-green-50/50 transition-all cursor-pointer border border-transparent hover:border-green-100 flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold text-gray-800 group-hover:text-green-600 text-sm">{item.name}</p>
                                                <p className="text-[10px] text-gray-500">{item.date || item.info || "N/A"}</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-green-600 transition-colors" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                ) : !isLoading && searchValue ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <Search className="w-16 h-16 mb-4 opacity-20" />
                        <p className="text-xl font-light">No results found for "{searchValue}"</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default AestheticSearchOverlay;

